'use client';

import { confirmModalAtom } from '@/atom/modal';
import { SurveyCGType, SurveyStatusCGType } from '@/types/graphql/root';
import { signinGuestUuid } from '@/utils/actions';
import { history } from '@/utils/history';
import { useSetAtom } from 'jotai';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';

export default function useGuestBlockBackward({
  tripId,
  trip,
  isLoggedin,
}: {
  tripId: string;
  trip: SurveyCGType | null;
  isLoggedin: boolean;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const setConfirmModal = useSetAtom(confirmModalAtom);

  const isOpenedTrip = useMemo(() => {
    return trip?.status === SurveyStatusCGType.OpenedCGType;
  }, [trip]);

  const isOwnTrip = useMemo(() => {
    return trip?.isOwn === true;
  }, [trip]);

  const openLoginConfirmModal = (isGoHome: boolean) => {
    setConfirmModal({
      visible: true,
      title: '지금 떠나면 일정표가 사라져요.',
      message: '로그인 하고 일정표를 저장해보세요.',
      onClose: () => {
        setConfirmModal({ visible: false });
        if (isGoHome) {
          router.push('/');
        } else {
          router.back();
        }
      },
      onConfirm: async () => {
        setConfirmModal({ visible: false });
        const guestUuid = await signinGuestUuid();
        sessionStorage.setItem(
          'needSaveSurvey',
          JSON.stringify({
            surveyId: tripId,
            guestUuid,
          }),
        );
        window.history.replaceState(null, '', '/myTravels');
        window.location.href = `${
          process.env.NEXT_PUBLIC_ZZIMCAR_URL
        }user/signin?redirectUrl=${window.location.origin}`;
      },
      confirmButtonText: '로그인하기',
      closeButtonText: '나가기',
    });
  };

  useEffect(() => {
    if (!isOpenedTrip || isLoggedin || !isOwnTrip) return;

    const handlePopState = () => {
      const isBlocked = searchParams.get('block') === 'true';

      if (isBlocked) {
        openLoginConfirmModal(false);
      } else {
        setConfirmModal({ visible: false });
      }
    };

    const unlistenHistoryEvent = history?.listen(({ action }) => {
      if (action === 'POP') {
        handlePopState();
      }
    });

    return unlistenHistoryEvent;
  }, [isOpenedTrip, isOwnTrip, isLoggedin, searchParams]);

  useEffect(() => {
    if (!isOpenedTrip || isLoggedin || !isOwnTrip) return;

    if (searchParams.get('block') !== 'true') {
      window.history.pushState(null, '', '?block=true');
    }

    return () => {
      setConfirmModal({ visible: false });
    };
  }, [isOpenedTrip, isOwnTrip, isLoggedin]);

  const onClickHome = () => {
    if (!isOpenedTrip || isLoggedin || !isOwnTrip) {
      router.push('/');
    } else {
      openLoginConfirmModal(true);
    }
  };

  return { onClickHome };
}
